// src/App.js

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './components/auth/AuthContext';
import PrivateRoute from './components/common/PrivateRoute';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminLayout from './components/layout/AdminLayout';
import { ToastContainer } from 'react-toastify';
import { Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/PremiumToasts.scss';
import { showToast } from './components/common/toastService';

// Navbar
import Navbar from './components/common/Navbar';
import PWAStatus from './components/common/PWAStatus';
import CreatorBottomNav from './components/common/CreatorBottomNav';
import { consumeInstagramConnectionToast } from './utils/instagramConnect';

// Reservas
import MyReservations from './components/reservations/MyReservations';
import CreateReservation from './components/reservations/CreateReservation';
import BusinessReservations from './components/reservations/BusinessReservations';
import CreatorCalendar from './components/reservations/CreatorCalendar';
import BusinessCalendar from './components/reservations/BusinessCalendar';

// Feed
import Feed from './components/feed/Feed';

// Otros
import Home from './components/Home';
import Product from './components/Product';
import Brands from './components/Brands';
import Creators from './components/Creators';
import Partners from './components/Partners';
import HelpCenter from './components/HelpCenter';
import About from './components/About';
import Contact from './components/Contact';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import VerificationForm from './components/auth/VerificationForm';
import Dashboard from './components/dashboard/Dashboard';
import UserProfile from './components/profile/UserProfile';
import UserProfilePage from './components/profile/UserProfilePage';
import PasswordResetRequest from './components/auth/PasswordResetRequest';
import PasswordResetConfirm from './components/auth/PasswordResetConfirm';
import CompleteProfile from './components/onboarding/CompleteProfile';
import InstagramConnectionReturn from './components/instagram/InstagramConnectionReturn';
import InstagramOAuthRecovery from './components/instagram/InstagramOAuthRecovery';
import EditProfile from './components/profile/EditProfile';
import RoleSelection from './components/auth/RoleSelection';
import Footer from './components/layout/Footer';
import './App.scss';
import './styles/PhoneInput.scss';
import './components/common/LegacyUi.scss';

// Business Components
import CreateOffer from './components/business/CreateOffer';
import { FirstCollabActivation } from './components/business/first-collab/FirstCollabActivation';
import MyOffers from './components/business/MyOffers';
import OfferSummary from './components/business/OfferSummary';
import OfferApplications from './components/business/OfferApplications';  // <-- IMPORT CORRECTO
import CreatorDiscovery from './components/business/CreatorDiscovery';
import BusinessApplications from './components/business/BusinessApplications';
import EditOffer from './components/business/EditOffer';
import PaymentRequired from './components/business/PaymentRequired';
import PaymentPlans from './components/business/PaymentPlans';
import SubscriptionsManager from './components/business/SubscriptionsManager';
import TransactionHistory from './components/business/TransactionHistory';
import CampaignMetrics from './components/business/collabs/CampaignMetrics';
import CollaborationReport from './components/business/CollaborationReport';
import BusinessCollabJourney from './components/business/BusinessCollabJourney';

// Creator Components
import CreatorOfferView from './components/creator/CreatorOfferView'; // New import for creator offer view
import CreatorEmvReport from './components/creator/CreatorEmvReport';
import InstagramAccountSelect from './components/creator/InstagramAccountSelect'; // Import for Instagram account selection
import MyApplications from './components/creator/MyApplications'; // Import for creator applications dashboard
import CreatorWallet from './components/creator/CreatorWallet';
import PendingApproval from './components/creator/PendingApproval'; // Pending verification state
import InvitedCreatorDashboard from './components/creator/InvitedCreatorDashboard'; // Invited creator restricted view

// Admin Components
import BusinessVerifications from './components/admin/BusinessVerifications';
import CreatorVerifications from './components/admin/CreatorVerifications';
import InvitedCreators from './components/admin/InvitedCreators';
import PaymentManagement from './components/admin/PaymentManagement';
import PlanManagement from './components/admin/PlanManagement';
import UserManagement from './components/admin/UserManagement';
import SellerManagement from './components/admin/SellerManagement';
import MetaManagement from './components/admin/MetaManagement';
import SellerDashboard from './components/seller/SellerDashboard';
import AffiliateRedirect from './components/seller/AffiliateRedirect';
import AnalyticsProvider from './components/analytics/AnalyticsProvider';

// Campaigns
import CampaignMetricsPage from './components/campaigns/CampaignMetricsPage';

// Public Landing
import PublicCollabLanding from './components/public/PublicCollabLanding';

// Legal
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import DataDeletion from './components/legal/DataDeletion';
import TermsOfService from './components/legal/TermsOfService';
import CookiePolicy from './components/legal/CookiePolicy';

// Route Guard
import VerificationGuard from './components/common/VerificationGuard';

const RedirectToUserProfile = () => {
  const { currentUser } = useAuth();

  if (currentUser && currentUser.username) {
    return <Navigate to={`/profile/${currentUser.username}`} replace />;
  }
  return <Navigate to="/profile" replace />;
};

const AppContent = () => {
  const { pathname, search } = useLocation();
  const { t } = useTranslation('common');
  const { isAuthenticated } = useAuth();

  React.useEffect(() => {
    if (pathname === '/instagram/connected') return;

    const confirmation = consumeInstagramConnectionToast(`${pathname}${search || ''}`)
      || consumeInstagramConnectionToast(pathname);
    if (!confirmation) return;

    showToast({
      tone: 'success',
      title: t('instagram_connection.toast.success_title'),
      description: t('instagram_connection.toast.success_body'),
      autoClose: 10000,
      toastId: 'instagram-connected-to-foth',
    });
  }, [pathname, search, t]);

  const isInviteLanding = pathname.startsWith('/i/');
  const isAffiliateLanding = pathname.startsWith('/partners/') || pathname.startsWith('/affiliate/');
  // Every view scoped to one collab owns its own header/exit controls. Keep
  // the global navbar out until the user returns to the collabs index.
  const isCollabRoute = /^(?:\/business\/collabs\/|\/creator\/(?:collabs|offers|feed)\/)[^/]+(?:\/.*)?\/?$/.test(pathname);
  const isFirstCollabRoute = pathname === '/business/first-collab';
  const isStandaloneCollab = isCollabRoute || isFirstCollabRoute;
  // Pages that handle their own top padding/spacing (usually full-bleed or dark luxury pages)
  const isFullBleed = [
    '/', '/product', '/brands', '/creators', '/partners', '/contact',
    '/login', '/register', '/forgot-password', '/reset-password'
  ].includes(pathname) || isInviteLanding || isAffiliateLanding;

  const isAuthPage = [
    '/login', '/register', '/forgot-password', '/reset-password',
    '/verify-account', '/select-role', '/complete-profile',
    '/instagram/connected',
    '/business/pending-approval',
    '/creator/pending-approval',
    '/creator/verification-documents'
  ].includes(pathname);

  const isAdmin = pathname.startsWith('/console');
  const mainSpacingClass = isAdmin ? null : (isFullBleed || isStandaloneCollab ? "home-content" : "with-navbar-padding");

  return (
    <>
      {!isAdmin && !isAuthPage && !isInviteLanding && !isAffiliateLanding && !isStandaloneCollab && <Navbar />}
      <main className={clsx(
        "main-content", 
        mainSpacingClass,
        isAdmin && "admin-main-area",
        isAuthPage && "auth-main-area"
      )}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/product" element={<Product />} />
          <Route path="/brands" element={<Brands />} />
          <Route path="/creators" element={<Creators />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register/creator" element={<Navigate to="/register" replace />} />
          <Route path="/register/business" element={<Navigate to="/register" replace />} />
          <Route path="/verify-account" element={<VerificationForm />} />

          <Route path="/partners/:code" element={<AffiliateRedirect />} />
          <Route path="/affiliate/:code" element={<AffiliateRedirect />} />

          {/* Legal Routes */}
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/select-role" element={<PrivateRoute><RoleSelection /></PrivateRoute>} />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <Dashboard />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/complete-profile"
              element={
                <PrivateRoute>
                  <CompleteProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/instagram/connected"
              element={
                <PrivateRoute>
                  <InstagramConnectionReturn />
                </PrivateRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <UserProfile />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/creator/profile"
              element={
                <PrivateRoute>
                  <RedirectToUserProfile />
                </PrivateRoute>
              }
            />

            {/* Admin rutas han sido trasladadas hacia abajo dentro del AdminLayout */}
            {/* Rutas de Business */}
            <Route
              path="/business/verification/status"
              element={<Navigate to="/business/pending-approval" replace />}
            />
            <Route
              path="/business/verification/documents"
              element={<Navigate to="/complete-profile" replace />}
            />
            <Route
              path="/business/pending-approval"
              element={
                <PrivateRoute>
                  <PendingApproval />
                </PrivateRoute>
              }
            />
            <Route
              path="/business/create-collab"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <CreateOffer />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/offers/create"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <CreateOffer />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/create-paid-collab"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <CreateOffer mode="paid" />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/first-collab"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <FirstCollabActivation />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/create-offer"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <CreateOffer />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/collabs"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <MyOffers />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/creators/discovery"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <CreatorDiscovery />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/offers"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <MyOffers />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/collabs/:id/summary"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <BusinessCollabJourney />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/collabs/:id/journey"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <BusinessCollabJourney />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/collabs/:id/metrics"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <CampaignMetrics />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/collabs/:id/edit"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <EditOffer />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/collabs/preview"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <OfferSummary isPreview={true} />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            {/* NUEVO: ver las postulaciones de UNA oferta */}
            <Route
              path="/business/collabs/:id/applications"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <OfferApplications />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />

            {/* Admin Routes wrapped in AdminLayout */}
            <Route element={<PrivateRoute adminOnly={true}><AdminLayout /></PrivateRoute>}>
              <Route path="/console/dashboard" element={<AdminDashboard />} />
              <Route path="/console/business-verifications" element={<BusinessVerifications />} />
              <Route path="/console/creator-verifications" element={<CreatorVerifications />} />
              <Route path="/console/invited-creators" element={<InvitedCreators />} />
              <Route path="/console/payments" element={<PaymentManagement />} />
              <Route path="/console/sellers" element={<SellerManagement />} />
              <Route path="/console/users" element={<UserManagement />} />
              <Route path="/console/plans" element={<PlanManagement />} />
              <Route path="/console/meta" element={<MetaManagement />} />
            </Route>

            {/* Rutas de verificación de creadores */}
            <Route
              path="/creator/verification/status"
              element={<Navigate to="/creator/pending-approval" replace />}
            />
            <Route
              path="/business/collabs/:id/report"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <CollaborationReport />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/creator/verification/documents"
              element={<Navigate to="/complete-profile" replace />}
            />
            {/* Route for Instagram-enabled verification documents */}
            <Route
              path="/creator/verification-documents"
              element={<Navigate to="/complete-profile" replace />}
            />

            {/* Route for Instagram account selection */}
            <Route
              path="/creator/instagram-account-select"
              element={
                <PrivateRoute>
                  <InstagramAccountSelect />
                </PrivateRoute>
              }
            />

            {/* Reservas */}
            <Route
              path="/creator/reservations"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <MyReservations />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/creator/calendar"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <CreatorCalendar />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/creator/reservations/create/:offerId"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <CreateReservation />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/business/reservations"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <BusinessReservations />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/business/calendar"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <BusinessCalendar />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />

            {/* Feed para creators */}
            <Route
              path="/creator/feed"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <Feed />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/creator/offers"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <Feed />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />

            {/* Creator Applications Dashboard */}
            <Route
              path="/creator/applications"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <MyApplications />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />

            {/* Pending Approval Page */}
            <Route
              path="/creator/pending-approval"
              element={
                <PrivateRoute>
                  <PendingApproval />
                </PrivateRoute>
              }
            />

            {/* Invited Creator Dashboard */}
            <Route
              path="/creator/invited/:collabId"
              element={
                <PrivateRoute>
                  <VerificationGuard allowInvited={true} allowedRoles={['creator']}>
                    <InvitedCreatorDashboard />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/create-paid-collab"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <CreateOffer mode="paid" />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/creator/wallet"
              element={
                <PrivateRoute>
                  <VerificationGuard allowInvited={true}>
                    <CreatorWallet />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/creator/invited"
              element={
                <PrivateRoute>
                  <VerificationGuard allowInvited={true} allowedRoles={['creator']}>
                    <InvitedCreatorDashboard />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />

            {/* Creator Offers & Detail Views */}
            <Route
              path="/creator/collabs/:id/report"
              element={
                <PrivateRoute>
                  <VerificationGuard allowInvited={true}>
                    <CreatorEmvReport />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/creator/offers/:id"
              element={
                <PrivateRoute>
                  <VerificationGuard allowInvited={true}>
                    <CreatorOfferView />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/creator/collabs/:id"
              element={
                <PrivateRoute>
                  <VerificationGuard allowInvited={true}>
                    <CreatorOfferView />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/creator/feed/:id"
              element={
                <PrivateRoute>
                  <VerificationGuard allowInvited={true}>
                    <CreatorOfferView />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/seller/dashboard"
              element={
                <PrivateRoute>
                  <SellerDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/business/applications"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <BusinessApplications />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />

            <Route
              path="/edit-profile"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <EditProfile />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/profile/:username"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <UserProfilePage />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route path="/forgot-password" element={<PasswordResetRequest />} />
            <Route path="/reset-password/:userId/:code" element={<PasswordResetConfirm />} />

            {/* New routes for business */}
            <Route
              path="/business/payment/required"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <PaymentRequired />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/collabs/:id/payment"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <PaymentRequired />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/payment/plans"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <PaymentPlans />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/plans"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <PaymentPlans />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/subscriptions"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <SubscriptionsManager />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/business/transactions"
              element={
                <PrivateRoute>
                  <VerificationGuard>
                    <TransactionHistory />
                  </VerificationGuard>
                </PrivateRoute>
              }
            />
            {/* Campaign Metrics Demo Route */}
            <Route path="/campaigns/metrics" element={<CampaignMetricsPage />} />

            {/* Public Collab Invitation Route */}
            <Route path="/i/:inviteToken" element={<PublicCollabLanding />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
        {!isAdmin && !isAuthPage && !isInviteLanding && !isAffiliateLanding && !isAuthenticated && <Footer />}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          limit={4}
          hideProgressBar={false}
          newestOnTop
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          draggableDirection="y"
          pauseOnHover
          transition={Slide}
        />
        <PWAStatus />
        {pathname !== '/instagram/connected' && <InstagramOAuthRecovery />}
        <CreatorBottomNav />
      </>
    );
};

const DesignSystemCatalog = process.env.NODE_ENV === 'development'
  ? React.lazy(() => import('./components/common/design-system/DesignSystemCatalog'))
  : null;

function App() {
  // Isolated, development-only showcase: no auth provider or API calls.
  if (DesignSystemCatalog && window.location.pathname === '/__design-system') {
    return <Router><React.Suspense fallback={null}><DesignSystemCatalog /></React.Suspense></Router>;
  }
  return (
    <AuthProvider>
      <Router>
        <AnalyticsProvider>
          <AppContent />
        </AnalyticsProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
